import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SignupGateProvider } from "@/context/SignupGate";
import { ThemeProvider } from "@/context/ThemeContext";
import { DataSaverProvider } from "@/context/DataSaverContext";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Feed from "@/pages/Feed";import Dashboard from "@/pages/Dashboard";
import Leaderboard from "@/pages/Leaderboard";
import PostContent from "@/pages/PostContent";
import Editor from "@/pages/Editor";
import PayPost from "@/pages/PayPost";
import Admin from "@/pages/Admin";
import Referrals from "@/pages/Referrals";
import Withdraw from "@/pages/Withdraw";
import Inbox from "@/pages/Inbox";
import Market from "@/pages/Market";
import Profile from "@/pages/Profile";
import Discover from "@/pages/Discover";
import Watch from "@/pages/Watch";
import Alerts from "@/pages/Alerts";
import BuyPoints from "@/pages/BuyPoints";
import WalletPage from "@/pages/Wallet";
import GamesPage from "@/pages/Games";
import LegalHome, { LegalRound, LegalResults, LegalLeaderboard, LegalStandings, LegalReview } from "@/pages/LegalGame";
import PaystackCallback from "@/pages/PaystackCallback";
import TermsOfService from "@/pages/TermsOfService";
import LegalPage from "@/pages/Legal";
import FindFriends from "@/pages/FindFriends";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import PostJob from "@/pages/PostJob";
import JobApplicants from "@/pages/JobApplicants";
import CareerProfile from "@/pages/CareerProfile";
import Candidates from "@/pages/Candidates";
import Messages from "@/pages/Messages";
import ApplicationDetail from "@/pages/ApplicationDetail";
import Partnerships, { PartnershipDetail, CreatePartnership, PartnershipRequests, PartnershipChat } from "@/pages/Partnerships";
import FindSchool, { SchoolDetail } from "@/pages/FindSchool";
import SchoolSystem, { TeacherPortal, HeadteacherPortal, ParentPortal } from "@/pages/SchoolSystem";
import Gated from "@/components/Gated";
import ProtectedRoute from "@/components/ProtectedRoute";
import GuestOrAuthShell from "@/components/GuestOrAuthShell";
import PhoneCollector from "@/components/PhoneCollector";
import InstallPrompt from "@/components/InstallPrompt";
import AppShell from "@/components/AppShell";

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect session_id in URL fragment synchronously during render (prevents race conditions)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Root path: guests + signed-in users go straight to the Feed. The
          Landing page lives at /welcome and is still reachable via the
          SignupGate CTA and the AppShell "Sign in" button for guests. */}
      <Route path="/" element={<Navigate to="/feed" replace />} />
      <Route path="/welcome" element={<Landing />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      {/* Paystack payment callback — public route because the browser may
          drop the auth cookie during the 3D-Secure / OTP roundtrip. Server
          re-authenticates via the standard session cookie when possible;
          the reference-based verify endpoint is safe either way (checks
          user_id match on the purchase row). */}
      <Route path="/wallet/topup/callback" element={<PaystackCallback />} />
      {/* Alias — some Paystack dashboards were configured with the shorter
          `/callback` URL. Keep both routes wired to the same handler so an
          incorrect dashboard setting doesn't strand paid users on a 404. */}
      <Route path="/callback" element={<PaystackCallback />} />

      {/* Guest-accessible routes. Feed + Watch let unauthenticated visitors
          browse and watch up to the guest view limit before signing up.
          Every gated action (like/comment/save/follow/sound/friends/etc.)
          triggers the SignupGate modal — see SignupGate.jsx. */}
      <Route element={<GuestOrAuthShell />}>
        <Route path="/feed" element={<Feed />} />
        <Route path="/w/:postId" element={<Watch />} />
      </Route>

      <Route
        element={
          <ProtectedRoute>
            <>
              <PhoneCollector />
              <AppShell />
            </>
          </ProtectedRoute>
        }
      >
        <Route path="/find-friends" element={<FindFriends />} />
        <Route path="/discover" element={<Discover />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/wallet" element={<WalletPage />} />
        <Route path="/games" element={<GamesPage />} />
        <Route path="/games/legal-championship" element={<LegalHome />} />
        <Route path="/games/legal-championship/play/:sessionId" element={<LegalRound />} />
        <Route path="/games/legal-championship/results/:sessionId" element={<LegalResults />} />
        <Route path="/games/legal-championship/leaderboard" element={<LegalLeaderboard />} />
        <Route path="/games/legal-championship/standings" element={<LegalStandings />} />
        <Route path="/games/legal-championship/review/:sessionId" element={<LegalReview />} />
        <Route path="/games/:gameId" element={<GamesPage />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/post" element={<PostContent />} />
        <Route path="/editor/:postId" element={<Editor />} />
        <Route path="/pay/:postId" element={<PayPost />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/withdraw" element={<Withdraw />} />
        <Route path="/buy-points" element={<BuyPoints />} />
        <Route path="/inbox" element={<Inbox />} />
        <Route path="/market" element={<Market />} />
        <Route path="/u/:userId" element={<Profile />} />
        <Route path="/admin" element={<Admin />} />
        {/* Jobs & Careers module (gated by points) */}
        <Route path="/jobs" element={<Gated feature="jobs"><Jobs /></Gated>} />
        <Route path="/jobs/post" element={<Gated feature="jobs"><PostJob /></Gated>} />
        <Route path="/jobs/edit/:jobId" element={<Gated feature="jobs"><PostJob /></Gated>} />
        <Route path="/jobs/mine/:jobId" element={<Gated feature="jobs"><JobApplicants /></Gated>} />
        <Route path="/jobs/:jobId" element={<Gated feature="jobs"><JobDetail /></Gated>} />
        <Route path="/career" element={<CareerProfile />} />
        <Route path="/career/:userId" element={<CareerProfile />} />
        <Route path="/candidates" element={<Gated feature="jobs"><Candidates /></Gated>} />
        <Route path="/applications/:appId" element={<ApplicationDetail />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/messages/:appId" element={<Messages />} />
        {/* Partnership Network module (gated by points) */}
        <Route path="/partnerships" element={<Gated feature="partners"><Partnerships /></Gated>} />
        <Route path="/partnerships/new" element={<Gated feature="partners"><CreatePartnership /></Gated>} />
        <Route path="/partnerships/edit/:pid" element={<Gated feature="partners"><CreatePartnership /></Gated>} />
        <Route path="/partnerships/mine/:pid/requests" element={<Gated feature="partners"><PartnershipRequests /></Gated>} />
        <Route path="/partnerships/requests/:rid" element={<Gated feature="partners"><PartnershipChat /></Gated>} />
        <Route path="/partnerships/:pid" element={<Gated feature="partners"><PartnershipDetail /></Gated>} />
        {/* Find School (gated by points) */}
        <Route path="/schools" element={<Gated feature="schools"><FindSchool /></Gated>} />
        <Route path="/schools/:sid" element={<Gated feature="schools"><SchoolDetail /></Gated>} />

        {/* School Management & Communication System */}
        <Route path="/school-system" element={<SchoolSystem />} />
        <Route path="/school-system/:schoolId/teacher" element={<TeacherPortal />} />
        <Route path="/school-system/:schoolId/headteacher" element={<HeadteacherPortal />} />
        <Route path="/school-system/parent/:studentId" element={<ParentPortal />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#0A0A0A] text-white">
      <BrowserRouter>
        <ThemeProvider>
          <DataSaverProvider>
            <AuthProvider>
              <SignupGateProvider>
                <AppRouter />
                <Toaster theme="dark" position="top-center" />
                <InstallPrompt />
              </SignupGateProvider>
            </AuthProvider>
          </DataSaverProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
